import React from 'react';
import { User, Settings, Bell, Palette, LogOut, Keyboard, FileText, Timer, Wrench } from 'lucide-react';
import { NavLink } from 'react-router-dom';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { PRESENCE_STATUS_OPTIONS } from './presenceStatusConfig';
import { getUserDisplayName, getUserHandle } from './utils';
import { useUserMenu } from './useUserMenu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

type UserMenuProps = ReturnType<typeof useUserMenu> & {
  compact?: boolean;
  onOpenLogs?: () => void;
  logErrorCount?: number;
  onOpenFocusTimer?: () => void;
  focusTimerActive?: boolean;
  enableDevTools?: boolean;
};

export function UserMenu(props: UserMenuProps) {
  const {
    user,
    compact = false,
    presenceStatus,
    statusColorClass,
    handleStatusChange,
    openProfile,
    handleSettingsClick,
    handleNotificationsClick,
    handleKeyboardShortcutsClick,
    handleThemeToggle,
    themeLabel,
    logout,
    userHandle,
    onOpenLogs,
    logErrorCount,
    onOpenFocusTimer,
    focusTimerActive,
    enableDevTools,
  } = props;
  const displayName = getUserDisplayName(user);
  const resolvedHandle = userHandle || getUserHandle(user);
  const tooltipLabel = resolvedHandle ? `Signed in as @${resolvedHandle}` : 'User menu';

  return (
    <DropdownMenu>
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <DropdownMenuTrigger asChild>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className={cn('flex items-center gap-2 rounded-full border border-border/60 bg-muted/40 px-3 py-1.5 h-auto text-sm font-medium', compact && 'h-11 w-11 shrink-0 p-0')}
                aria-label="Open user menu"
                data-user-handle={resolvedHandle ? `@${resolvedHandle}` : undefined}
              >
                <span className={cn('h-2 w-2 rounded-full', statusColorClass)} />
                {compact ? <User className="h-5 w-5" aria-hidden="true" /> : displayName || (resolvedHandle ? `@${resolvedHandle}` : 'User')}
              </Button>
            </TooltipTrigger>
          </DropdownMenuTrigger>
          <TooltipContent side="bottom" align="end">
            {tooltipLabel}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <DropdownMenuContent align="end" className="w-56">
        {(displayName || resolvedHandle) ? (
          <>
            <DropdownMenuLabel className="flex flex-col gap-1">
              {displayName ? <span className="font-semibold">{displayName}</span> : null}
              {resolvedHandle ? <span className="text-xs text-muted-foreground">@{resolvedHandle}</span> : null}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
          </>
        ) : null}
        <DropdownMenuLabel>Status</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={presenceStatus} onValueChange={handleStatusChange}>
          {PRESENCE_STATUS_OPTIONS.map((status) => (
            <DropdownMenuRadioItem key={status.value} value={status.value}>
              <div className="flex items-center gap-2">
                <span className={cn('h-2 w-2 rounded-full', status.colorClass)} />
                {status.label}
              </div>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={openProfile}>
          <User className="mr-2 h-4 w-4" />
          View Profile
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleSettingsClick}>
          <Settings className="mr-2 h-4 w-4" />
          Settings
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleNotificationsClick}>
          <Bell className="mr-2 h-4 w-4" />
          Notifications Settings
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleThemeToggle}>
          <Palette className="mr-2 h-4 w-4" />
          {themeLabel} Theme
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleKeyboardShortcutsClick}>
          <Keyboard className="mr-2 h-4 w-4" />
          Keyboard Shortcuts
        </DropdownMenuItem>
        {onOpenLogs && (
          <DropdownMenuItem onClick={onOpenLogs}>
            <FileText className="mr-2 h-4 w-4" />
            Application Logs
            {logErrorCount && logErrorCount > 0 ? (
              <span className="ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-[10px] font-medium text-destructive-foreground">
                {logErrorCount}
              </span>
            ) : null}
          </DropdownMenuItem>
        )}
        {onOpenFocusTimer && (
          <DropdownMenuItem onClick={onOpenFocusTimer}>
            <Timer className={cn("mr-2 h-4 w-4", focusTimerActive && "text-primary")} />
            Focus Timer
            {focusTimerActive && (
              <span className="ml-auto inline-flex h-2 w-2 rounded-full bg-primary" />
            )}
          </DropdownMenuItem>
        )}
        {enableDevTools && (
          <DropdownMenuItem asChild>
            <NavLink to="/dev" className="flex items-center">
              <Wrench className="mr-2 h-4 w-4" />
              Developer Tools
            </NavLink>
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={logout} className="text-destructive focus:text-destructive">
          <LogOut className="mr-2 h-4 w-4" />
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
